import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { FormGroup } from 'reactstrap';

import { EmbeddedForm, QuizResults, formShape } from '@techchange/components';
import { DangerAlert, API_BASE_URL, useLanguageContext } from '@techchange/components';
import { messages } from '../../../../i18n';

import FormDisplayEntryPagination from '../form/components/FormDisplayEntryPagination';

import QuizDisplayResultsFailMsg from './components/QuizDisplayResultsFailMsg';
import QuizDisplayResultsPassMsg from './components/QuizDisplayResultsPassMsg';

const axiosClient = axios.create({
	baseURL: API_BASE_URL.FORM_ENTRIES,
	withCredentials: true,
});

const propTypes = {

	/**
	 * Flag indicating if the content title should be visible.
	 */
	showTitle: PropTypes.bool,

	/**
	 * The associated quiz form to display.
	 */
	form: formShape.isRequired,

	/**
	 * The current user's ID.
	 */
	userId: PropTypes.string,

};

const ContentDisplay = (props) => {

	const {
		showTitle,
		form,
		userId,
	} = props;

	const { chooseContentFromLanguage } = useLanguageContext();

	// Keep track of all the entries for the current user for this quiz form
	const [formEntries, setFormEntries] = useState(null);

	// Keep track of the current form entry to view/create/edit
	const [currentEntry, setCurrentEntry] = useState(null);

	const [isEditing, setIsEditing] = useState(false);

	const [showErrorMsg, setShowErrorMsg] = useState(false);

	const refErrorAlert = useRef();

	/**
	 * Handles saving an entry, i.e. both creation and updates.
	 *
	 * @param {object} data  The form entry data.
	 */
	const handleSave = (data) => {

		data.user_id = userId;

		let promise;

		// Check if updating an existing entry for the current user
		if (data.id) {

			promise = axiosClient.patch(`/${data.id}`, data);

		// Otherwise, create a new entry
		} else {

			promise = axiosClient.post('/', data);

		}

		// Reset the API success/error messages
		setShowErrorMsg(false);

		promise.then((response) => {

			const updatedEntry = Object.assign({}, response.data);

			setFormEntries((prev) => {

				// Create a copy of all the previous form entries for the user for this quiz form
				const updatedFormEntries = [...prev];

				const existingIndex = updatedFormEntries.findIndex(
					(entry) => entry.id === updatedEntry.id);

				// Check if the user updated an existing form entry
				if (existingIndex >= 0) {

					updatedFormEntries[existingIndex] = updatedEntry;

				// Otherwise, a new form entry was created
				} else {

					updatedFormEntries.push(updatedEntry);

				}

				return updatedFormEntries;

			});

			setCurrentEntry(updatedEntry);

			setIsEditing(false);

		}).catch((error) => {

			console.error(error);
			setShowErrorMsg(true);

		});

	};

	/**
	 * Handles clicking to view a specific entry for the current user for this quiz form.
	 *
	 * @param {number} entryIndex  The index of the entry to view.
	 */
	const onGoToEntry = (entryIndex) => {

		// Update the current entry
		setCurrentEntry(Object.assign({}, formEntries[entryIndex]));

		setIsEditing(false);

		setShowErrorMsg(false);

	};

	/**
	 * Handles clicking to retry with a new entry for the current user for this quiz form.
	 */
	const onRetry = () => {

		// Reset the current entry
		setCurrentEntry(null);

		setIsEditing(true);

		setShowErrorMsg(false);

	};

	/**
	 * Handles clicking to cancel the creation or update of an entry.
	 */
	const handleCancel = () => {

		// Check if the user cancelled creating a new entry
		if (!currentEntry) {

			// Reset the current entry to be the most recent entry for the user for this quiz form
			setCurrentEntry(Object.assign({}, formEntries[formEntries.length - 1]));

		}

		setIsEditing(false);

	};

	// Fetch all the form entries for the current user for this quiz form
	useEffect(() => {

		if (!formEntries) {

			axiosClient.get('/', {
				params: {
					form_id: form.id,
					user_id: userId,
					scope: form.scope,
					scope_id: form.scope_id,
					sort_by: 'created_at',
					sort_order: 'ASC',
				}
			}).then((response) => {

				const allEntries = (response && response.data) || [];

				const mostRecentEntry = allEntries[allEntries.length - 1];

				setCurrentEntry(mostRecentEntry);

				setIsEditing(!mostRecentEntry);

				setFormEntries(allEntries);

			});

		}

	}, [form, formEntries, userId]);

	// Auto-scroll to the error message (if needed)
	useEffect(() => {

		if (showErrorMsg && refErrorAlert?.current) {
			refErrorAlert.current.scrollIntoView({ behavior: 'smooth' });
		}

	}, [showErrorMsg]);

	// Check if we are still loading the associated form entries
	if (!formEntries) {
		return <h1 className="p-3"><i className="fas fa-circle-notch fa-spin" /></h1>;
	}

	// Check if the learner has passed this quiz form
	const hasPassedQuiz =
		formEntries.length > 0 &&
		formEntries[formEntries.length - 1] &&
		formEntries[formEntries.length - 1].quiz_result_passed;

	// Check if the learner can retry with a new entry for this quiz form
	const canRetryQuiz =
		!isEditing &&
		Boolean(currentEntry) &&
		!hasPassedQuiz &&
		form.allow_multiple_submissions;

	// Check if the learner has already submitted multiple entries for this quiz form
	const showMultipleEntriesPagination = Boolean(currentEntry) && formEntries.length > 1;

	const index =
		formEntries.findIndex((entry) => currentEntry && entry.id === currentEntry.id);

	// Get the translated content for the form
	const formContent = chooseContentFromLanguage(form.form_contents);

	return (
		<div className="tc-content-builder-form-entry tc-content-builder-quiz-display">
			<FormGroup>
				{showTitle && formContent?.name &&
					<h5 className="tc-content-builder-form-title">
						{formContent.name}
					</h5>
				}
				{formContent?.description &&
					<span className="tc-content-builder-form-description">
						{formContent.description}
					</span>
				}
			</FormGroup>
			{showErrorMsg &&
				<div ref={refErrorAlert}>
					<DangerAlert>
						<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_ENTRY_MSG_TEXT_SUBMIT_ERROR} />
					</DangerAlert>
				</div>
			}
			{currentEntry && !currentEntry.quiz_result_passed &&
				<QuizDisplayResultsFailMsg
					form={form}
					formEntry={currentEntry}
					onRetry={canRetryQuiz ? onRetry : null} />
			}
			{currentEntry && currentEntry.quiz_result_passed &&
				<QuizDisplayResultsPassMsg
					form={form}
					formEntry={currentEntry} />
			}
			{showMultipleEntriesPagination &&
				<div className="tc-content-builder-form-entry-multiple-entries-pagination">
					<FormDisplayEntryPagination
						formEntries={formEntries}
						currentIndex={index}
						type={form.type}
						goToEntry={onGoToEntry} />
				</div>
			}
			{currentEntry &&
				<div className="tc-content-builder-form-entry-metadata">
					<span className="timestamp">
						<FormattedMessage
							{...messages.TC_COMPONENTS_GENERAL_TEXT_SUBMITTED_DATE}
							values={{
								date: moment(currentEntry.updated_at).format('MMMM D, YYYY h:mm A'),
							}} />
					</span>
				</div>
			}
			{isEditing &&
				<EmbeddedForm
					key={currentEntry?.id}
					form={form}
					formEntry={currentEntry}
					isEditing
					onCancel={formEntries.length >= 1 ? handleCancel : null}
					onSave={handleSave} />
			}
			{!isEditing &&
				<QuizResults
					form={form}
					formEntry={currentEntry}
					showCorrectAnswers={form.quiz_result_show_correct_answers}
					showFeedback />
			}
		</div>
	);

};

ContentDisplay.propTypes = propTypes;

export default ContentDisplay;
